import { useCalcPnlChartCategoriesWithProducingLosses } from "components/Dashboard/PnlSummaryChart/calcPnlChartCategoriesWithProducingLosses";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { FONT_SIZE_EXTRA_SMALL, PNL_COLORS } from "constants/constants";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useMemo } from "react";
import styled from "styled-components/macro";
import { PnlCategoryLabelsEnum } from "types/enums";
import { Process } from "types/types";
import { getExpandedTileContentHeights } from "./ExpandedTileModules";
import { useSelector } from "store/useSelector";
import { FilterAlt } from "@mui/icons-material";
import { Tooltip, styled as muiStyled } from "@mui/material";
import { useProcessTilesFilterState } from "pages/DashboardPages/ProcessTilesControls/useProcessTilesFilterState";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";

/** An Expanded Tile module that displays the top 3 PNL losses */
export const TopLossesModule = ({ process }: { process: Process }) => {
  const { isLargeTiles, tileScaleMultiplier } = useSettings();
  const [
    {
      general: { expandedProcessTileCompactMode },
    },
  ] = useFlightConfig();

  // Get the filter state to apply top losses filtering
  const [filterState] = useProcessTilesFilterState();
  const { filterByTopLosses } = filterState;
  const { translate } = useFlightTranslation();

  // Get the necessary data for filter display
  const calcPnlChartCategories = useCalcPnlChartCategoriesWithProducingLosses();
  const dataDumpsByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );

  // Access the dataDump directly using the process ID as key
  const dataDumpWithProducingLosses = useMemo(() => {
    return dataDumpsByProcess ? dataDumpsByProcess[process.id] : undefined;
  }, [dataDumpsByProcess, process]);

  // Check if filter is active
  const isFilterActive = useMemo(() => {
    return filterByTopLosses && filterByTopLosses.length > 0;
  }, [filterByTopLosses]);

  // Generate the tooltip content describing the current filter
  const filterTooltipContent = useMemo(() => {
    if (!isFilterActive) {
      return translate("No active filters");
    }

    // Special case for when filterByTopLosses is an empty array (all selected)
    if (filterByTopLosses && filterByTopLosses.length === 0) {
      return translate("Showing all top losses");
    }

    // Get all possible top loss names from the dataDump
    const allPossibleLosses: string[] = [];
    if (dataDumpWithProducingLosses && calcPnlChartCategories) {
      const pnlCategories = calcPnlChartCategories(dataDumpWithProducingLosses);
      pnlCategories.forEach(category => {
        // Skip producing category since we're looking for losses
        if (
          category.title !== PnlCategoryLabelsEnum.producing &&
          category.bars
        ) {
          category.bars.forEach(bar => {
            if (bar.title && !allPossibleLosses.includes(bar.title)) {
              allPossibleLosses.push(bar.title);
            }
          });
        }
      });
    }

    // Determine which losses are being filtered out
    // If filter is present, any loss not in the filter is considered filtered out
    const irrelevantLosses = allPossibleLosses.filter(loss => {
      if (filterByTopLosses) {
        return !filterByTopLosses.includes(loss);
      } else {
        return false;
      }
    });

    if (irrelevantLosses.length === 0) {
      return translate("No filters applied to this process");
    }

    return (
      <>
        <strong>{translate("Filtered out:")} </strong>
        <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
          {irrelevantLosses.map(loss => (
            <li key={loss}>{loss}</li>
          ))}
        </ul>
      </>
    );
  }, [
    filterByTopLosses,
    isFilterActive,
    translate,
    calcPnlChartCategories,
    dataDumpWithProducingLosses,
  ]);

  // Extract the top losses from all categories and apply filtering
  const topLosses = useMemo(() => {
    if (!process || !dataDumpWithProducingLosses) {
      return [];
    }

    const pnlCategories = calcPnlChartCategories(dataDumpWithProducingLosses);

    // Calculate total time (sum of all categories including producing)
    let totalTime = 0;
    pnlCategories.forEach(category => {
      if (category.time) {
        totalTime += category.time;
      }
    });

    // Flatten all loss bars from all categories
    const allLosses = pnlCategories.flatMap(category =>
      // Filter out producing category since we're looking for losses
      category.title !== PnlCategoryLabelsEnum.producing ? category.bars : []
    );

    // Apply the top losses filter if any are selected
    let filteredLosses = allLosses;
    if (
      filterByTopLosses &&
      filterByTopLosses.length > 0 &&
      !filterByTopLosses.includes("SELECT_ALL")
    ) {
      filteredLosses = allLosses.filter(loss =>
        filterByTopLosses.includes(loss.title)
      );
    }

    // Sort by time in descending order and take top 3
    const sortedLosses = filteredLosses
      .sort((a, b) => b.time - a.time)
      .slice(0, 3);

    return sortedLosses.map(loss => {
      // Calculate percentage safely to prevent NaN
      const percent = totalTime > 0 ? (loss.time / totalTime) * 100 : 0;

      return {
        ...loss,
        // Ensure percent is a valid number
        percent: isNaN(percent) ? 0 : percent,
        // Scale width relative to total time (losses + producing) instead of just max loss
        relativeWidth: totalTime > 0 ? (loss.time / totalTime) * 100 : 0,
      };
    });
  }, [
    dataDumpWithProducingLosses,
    calcPnlChartCategories,
    process,
    filterByTopLosses,
  ]);

  return (
    <TopLossesModuleStyles
      $isLargeTiles={isLargeTiles}
      $compact={expandedProcessTileCompactMode}
      $tileScaleMultiplier={tileScaleMultiplier ** 0.45}
    >
      {/* Filter indicator with tooltip */}
      {isFilterActive && (
        <HtmlTooltip title={filterTooltipContent} placement="top" arrow>
          <div
            style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              zIndex: 3,
              cursor: "help",
              backgroundColor: "rgba(255,255,255,0.8)",
              borderRadius: "50%",
              width: "16px",
              height: "16px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            }}
          >
            <FilterAlt style={{ fontSize: "12px", color: "#666" }} />
          </div>
        </HtmlTooltip>
      )}

      {dataDumpWithProducingLosses ? (
        <div className="losses-container">
          {topLosses.length > 0 ? (
            <div className="losses-list">
              {topLosses.map((loss, index) => (
                <LossRow
                  key={index}
                  title={loss.title}
                  category={loss.category}
                  time={loss.time}
                  percent={loss.percent ?? 0}
                  relativeWidth={loss.relativeWidth}
                />
              ))}
            </div>
          ) : (
            <div className="no-losses">No significant losses</div>
          )}
        </div>
      ) : (
        <Spinner width={10} />
      )}
    </TopLossesModuleStyles>
  );
};

// Custom tooltip with proper styling
const HtmlTooltip = muiStyled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 300,
    fontSize: "12px",
    border: "1px solid #dadde9",
    padding: "12px",
  },
}));

const TopLossesModuleStyles = styled.div<{
  $isLargeTiles: boolean;
  $compact: boolean;
  $tileScaleMultiplier: number;
}>`
  position: relative;
  height: ${props =>
    getExpandedTileContentHeights(props.$compact).topLosses[
      props.$isLargeTiles ? "lg" : "sm"
    ]}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;

  .losses-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: ${props => (props.$isLargeTiles ? 2 : 1) * 6}px;
    box-sizing: border-box;
  }

  .losses-list {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${props => (props.$isLargeTiles ? 2 : 1) * 2}px;
    padding: 0px;
  }

  .no-losses {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    height: 100%;
    font-size: ${props =>
      (props.$isLargeTiles ? 2 : 1) *
      FONT_SIZE_EXTRA_SMALL *
      props.$tileScaleMultiplier}px;
    color: #888;
  }
`;

/** A helper function to format milliseconds as a readable time string */
const formatTimeFromMs = (ms: number, shortFormat = false): string => {
  if (ms === 0) {
    return "0s";
  }

  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor(ms / (1000 * 60 * 60));

  if (shortFormat) {
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  } else {
    let result = "";
    if (hours > 0) {
      result += `${hours} hr${hours !== 1 ? "s" : ""} `;
    }
    if (minutes > 0 || hours > 0) {
      result += `${minutes} min${minutes !== 1 ? "s" : ""} `;
    }
    if (seconds > 0 || (hours === 0 && minutes === 0)) {
      result += `${seconds} sec${seconds !== 1 ? "s" : ""}`;
    }
    return result.trim();
  }
};

/** A row displaying a loss with tag name, duration, and percentage */
const LossRow = ({
  title,
  category,
  time,
  percent,
  relativeWidth,
}: {
  title: string;
  category: string;
  time: number;
  percent: number;
  relativeWidth: number;
}) => {
  const formattedTime = formatTimeFromMs(time, true);
  // Handle NaN, undefined, or null percent values
  const formattedPercent = isNaN(percent) ? "-" : `${Math.round(percent)}%`;
  const { isLargeTiles, tileScaleMultiplier } = useSettings();
  return (
    <Tooltip title={title} placement="top" disableInteractive>
      <LossRowStyles
        $category={category}
        $relativeWidth={relativeWidth}
        $isLargeTiles={isLargeTiles}
        $tileScaleMultiplier={tileScaleMultiplier ** 0.45}
      >
        <div className="row-content">
          <div className="tag-name">{title}</div>
          <div className="meta-container">
            <div className="duration">{formattedTime}</div>
            <div className="percent">{formattedPercent}</div>
          </div>
        </div>
        <div className="bar-background">
          <div className="bar-fill" />
        </div>
      </LossRowStyles>
    </Tooltip>
  );
};

const LossRowStyles = styled.div<{
  $category: string;
  $relativeWidth: number;
  $isLargeTiles: boolean;
  $tileScaleMultiplier: number;
}>`
  position: relative;
  width: 100%;
  margin-bottom: 2px;
  height: ${props => (props.$isLargeTiles ? 2 : 1) * 34}px;

  .row-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: ${props => (props.$isLargeTiles ? 2 : 1)}px
      ${props => (props.$isLargeTiles ? 2 : 1) * 6}px;
    cursor: default;
    box-sizing: border-box;
    overflow: hidden;
  }

  .tag-name {
    font-size: ${props =>
      (props.$isLargeTiles ? 2 : 1) *
      FONT_SIZE_EXTRA_SMALL *
      props.$tileScaleMultiplier}px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: ${props => (props.$isLargeTiles ? 2 : 1)}px;
    font-weight: 500;
  }

  .meta-container {
    display: flex;
    gap: ${props => (props.$isLargeTiles ? 2 : 1) * 8}px;
    font-size: ${props =>
      (props.$isLargeTiles ? 2 : 1) *
      FONT_SIZE_EXTRA_SMALL *
      props.$tileScaleMultiplier}px;
  }

  .duration,
  .percent {
    font-size: ${props =>
      (props.$isLargeTiles ? 2 : 1) *
      FONT_SIZE_EXTRA_SMALL *
      props.$tileScaleMultiplier}px;
    white-space: nowrap;
  }
  .percent {
    margin-left: auto;
  }

  .bar-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }

  .bar-fill {
    position: absolute;
    top: 0;
    left: 0;
    width: ${props => Math.min(Math.max(props.$relativeWidth, 1), 100)}%;
    height: 100%;
    background-color: ${props =>
      PNL_COLORS[props.$category] || PNL_COLORS.unknown};
    opacity: 0.2;
    border-radius: 3px;
  }
`;
